import { execSync } from 'node:child_process';
import chalk from 'chalk';
import ora from 'ora';
import { banner, logger } from '../core/logger.js';
import type { RunResult, StructuredFinding } from '../core/run-result.js';
import { summarize } from '../core/run-result.js';

interface PentestResult {
  url: string;
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  details?: string;
}

// Basic vulnerability tests
const PENTEST_CHECKS = [
  {
    name: 'XSS Reflected',
    test: (html: string) => /<script>alert\(1\)<\/script>|<img\s+src=x\s+onerror=alert/i.test(html),
    severity: 'critical' as const,
    message: 'Reflected XSS payload rendered in response',
  },
  {
    name: 'Open Redirect',
    test: (url: string, body: string) => body.includes('location.href') || body.includes('window.location'),
    severity: 'medium' as const,
    message: 'Potential open redirect detected',
  },
  {
    name: 'Missing Security Headers',
    test: (_url: string, _body: string, headers: Record<string, string>) => {
      const required = ['x-content-type-options', 'x-frame-options', 'strict-transport-security'];
      const missing = required.filter(h => !headers[h.toLowerCase()]);
      return missing.length > 2;
    },
    severity: 'low' as const,
    message: 'Missing critical security headers (X-Content-Type-Options, X-Frame-Options, HSTS)',
  },
  {
    name: 'Server Version Disclosure',
    test: (_url: string, _body: string, headers: Record<string, string>) => {
      return !!(headers['server'] && /apache|nginx|iis|tomcat/i.test(headers['server']));
    },
    severity: 'low' as const,
    message: 'Server version disclosed in response header',
  },
  {
    name: 'Directory Listing',
    test: (html: string) => /Index of\s*\/|<title>Directory Listing/i.test(html),
    severity: 'medium' as const,
    message: 'Directory listing enabled',
  },
  {
    name: 'Error Page Information Disclosure',
    test: (html: string) => /stack trace|traceback|exception|at\s+\w+\.\w+\(/i.test(html),
    severity: 'medium' as const,
    message: 'Error page reveals internal information',
  },
  // PRD 07: Additional checks
  {
    name: 'CORS Misconfiguration',
    test: (_url: string, _body: string, headers: Record<string, string>) => {
      const acao = headers['access-control-allow-origin'];
      return acao === '*' || acao === 'null';
    },
    severity: 'medium' as const,
    message: 'CORS allows any origin (Access-Control-Allow-Origin: *)',
  },
  {
    name: 'Cookie Security',
    test: (_url: string, _body: string, headers: Record<string, string>) => {
      const setCookie = headers['set-cookie'] || '';
      return setCookie.length > 0 && (!setCookie.includes('HttpOnly') || !setCookie.includes('Secure'));
    },
    severity: 'medium' as const,
    message: 'Cookies missing HttpOnly or Secure flags',
  },
  {
    name: 'Content Security Policy',
    test: (_url: string, _body: string, headers: Record<string, string>) => {
      return !headers['content-security-policy'];
    },
    severity: 'low' as const,
    message: 'No Content-Security-Policy header set',
  },
  {
    name: 'Sensitive Path Exposure',
    test: (html: string) => /\.env|wp-admin|phpinfo|\.git\/config|server-status/i.test(html),
    severity: 'high' as const,
    message: 'Response references sensitive paths or admin endpoints',
  },
];

export async function runPentest(rawUrl: string): Promise<RunResult> {
  const targetUrl = rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`;
  const results = await collectPentestResults(targetUrl);
  const structured: StructuredFinding[] = results.map((r) => ({
    type: r.type,
    severity: r.severity === 'low' ? 'low' : (r.severity as StructuredFinding['severity']),
    message: r.message,
    location: r.url,
    details: r.details ? { detail: r.details } : undefined,
  }));
  const counts = summarize(structured);
  return {
    type: 'pentest',
    target: targetUrl,
    findings: structured,
    severity_summary: counts,
    summary: structured.length === 0
      ? 'No vulnerabilities detected'
      : `${structured.length} issue(s): ${counts.critical}C ${counts.high}H ${counts.medium}M ${counts.low}L`,
  };
}

async function collectPentestResults(targetUrl: string): Promise<PentestResult[]> {
  const results: PentestResult[] = [];
  try {
    const resp = await fetch(targetUrl, { redirect: 'manual' });
    const headers: Record<string, string> = {};
    resp.headers.forEach((v, k) => { headers[k] = v; });
    const body = await resp.text();
    for (const check of PENTEST_CHECKS) {
      try {
        if (check.test(body, body, headers)) {
          results.push({ url: targetUrl, type: check.name, severity: check.severity, message: check.message });
        }
      } catch { /* skip */ }
    }
  } catch {
    return results;
  }

  const sqliPayloads = ["' OR 1=1--", "1' UNION SELECT NULL--", "' AND '1'='1"];
  for (const payload of sqliPayloads) {
    try {
      const testUrl = `${targetUrl}?id=${encodeURIComponent(payload)}`;
      const resp = await fetch(testUrl, { redirect: 'manual', signal: AbortSignal.timeout(5000) });
      const body = await resp.text();
      if (/sql\s+(syntax|error|exception)|mysql|postgres|ORA-\d+/i.test(body)) {
        results.push({ url: testUrl, type: 'SQL Injection', severity: 'critical', message: `SQL error with payload: ${payload}` });
      }
    } catch { /* skip */ }
  }

  const traversalPaths = ['../../etc/passwd', '..%2F..%2Fetc%2Fpasswd', '....//....//etc/passwd'];
  for (const path of traversalPaths) {
    try {
      const testUrl = `${targetUrl}/${path}`;
      const resp = await fetch(testUrl, { redirect: 'manual', signal: AbortSignal.timeout(5000) });
      const body = await resp.text();
      if (/root:.*:0:0:|daemon:.*:1:1:|nobody:.*:65534/i.test(body)) {
        results.push({ url: testUrl, type: 'Path Traversal', severity: 'critical', message: `Possible /etc/passwd disclosure: ${path}` });
      }
    } catch { /* skip */ }
  }

  const methods = ['OPTIONS', 'TRACE', 'DELETE', 'PUT'];
  for (const method of methods) {
    try {
      const resp = await fetch(targetUrl, { method, signal: AbortSignal.timeout(5000) });
      if (resp.status < 400 && method !== 'OPTIONS') {
        results.push({ url: targetUrl, type: `Unsafe HTTP Method: ${method}`, severity: 'medium', message: `${method} allowed (status ${resp.status})` });
      }
      if (method === 'OPTIONS') {
        const allow = resp.headers.get('allow');
        if (allow && /DELETE|PUT|TRACE/i.test(allow)) {
          results.push({ url: targetUrl, type: 'HTTP Methods', severity: 'low', message: `Allowed methods: ${allow}` });
        }
      }
    } catch { /* skip */ }
  }

  return results;
}

export async function pentestCommand(targetUrl: string): Promise<RunResult> {
  banner();

  // Ensure URL has protocol
  if (!targetUrl.startsWith('http')) {
    targetUrl = `https://${targetUrl}`;
  }

  logger.info(`Penetration testing ${chalk.white(targetUrl)}...\n`);

  const results: PentestResult[] = [];

  // Test 1: Basic response analysis
  const spinner = ora({ text: 'Fetching target...', color: 'green' }).start();
  try {
    const resp = await fetch(targetUrl, { redirect: 'manual' });
    const headers: Record<string, string> = {};
    resp.headers.forEach((v, k) => { headers[k] = v; });
    const body = await resp.text();

    spinner.succeed(`Got ${resp.status} response\n`);

    // Run all checks
    for (const check of PENTEST_CHECKS) {
      try {
        if (check.test(body, body, headers)) {
          results.push({
            url: targetUrl,
            type: check.name,
            severity: check.severity,
            message: check.message,
          });
        }
      } catch {
        // Skip check on error
      }
    }
  } catch (err) {
    spinner.fail(`Failed to reach target: ${(err as Error).message}`);
    console.log(chalk.gray('  Check the URL and try again.\n'));
    return {
      type: 'pentest',
      target: targetUrl,
      findings: [],
      severity_summary: { critical: 0, high: 0, medium: 0, low: 0, info: 0 },
      summary: `Failed to reach target: ${(err as Error).message}`,
      error: (err as Error).message,
    };
  }

  // Test 2: SQL injection probe
  const sqliSpinner = ora({ text: 'Testing SQL injection vectors...', color: 'green' }).start();
  const sqliPayloads = ["' OR 1=1--", "1' UNION SELECT NULL--", "' AND '1'='1"];
  for (const payload of sqliPayloads) {
    try {
      const testUrl = `${targetUrl}?id=${encodeURIComponent(payload)}`;
      const resp = await fetch(testUrl, { redirect: 'manual', signal: AbortSignal.timeout(5000) });
      const body = await resp.text();
      if (/sql\s+(syntax|error|exception)|mysql|postgres|ORA-\d+/i.test(body)) {
        results.push({
          url: testUrl,
          type: 'SQL Injection',
          severity: 'critical',
          message: `SQL error response detected with payload: ${payload}`,
        });
      }
    } catch {
      // Skip on timeout/network error
    }
  }
  sqliSpinner.succeed('SQL injection tests complete\n');

  // Test 3: Path traversal probe
  const pathSpinner = ora({ text: 'Testing path traversal...', color: 'green' }).start();
  const traversalPaths = ['../../etc/passwd', '..%2F..%2Fetc%2Fpasswd', '....//....//etc/passwd'];
  for (const path of traversalPaths) {
    try {
      const testUrl = `${targetUrl}/${path}`;
      const resp = await fetch(testUrl, { redirect: 'manual', signal: AbortSignal.timeout(5000) });
      const body = await resp.text();
      if (/root:.*:0:0:|daemon:.*:1:1:|nobody:.*:65534/i.test(body)) {
        results.push({
          url: testUrl,
          type: 'Path Traversal',
          severity: 'critical',
          message: `Possible /etc/passwd disclosure via: ${path}`,
        });
      }
    } catch {
      // Skip on timeout/network error
    }
  }
  pathSpinner.succeed('Path traversal tests complete\n');

  // Test 4: HTTP methods
  const methodSpinner = ora({ text: 'Testing HTTP methods...', color: 'green' }).start();
  const methods = ['OPTIONS', 'TRACE', 'DELETE', 'PUT'];
  for (const method of methods) {
    try {
      const resp = await fetch(targetUrl, { method, signal: AbortSignal.timeout(5000) });
      if (resp.status < 400 && method !== 'OPTIONS') {
        results.push({
          url: targetUrl,
          type: `Unsafe HTTP Method: ${method}`,
          severity: 'medium',
          message: `${method} method is allowed (status ${resp.status})`,
        });
      }
      if (method === 'OPTIONS') {
        const allow = resp.headers.get('allow');
        if (allow && /DELETE|PUT|TRACE/i.test(allow)) {
          results.push({
            url: targetUrl,
            type: 'HTTP Methods',
            severity: 'low',
            message: `Allowed methods: ${allow}`,
          });
        }
      }
    } catch {
      // Skip on error
    }
  }
  methodSpinner.succeed('HTTP method tests complete\n');

  const structured: StructuredFinding[] = results.map((r) => ({
    type: r.type,
    severity: r.severity as StructuredFinding['severity'],
    message: r.message,
    location: r.url,
    details: r.details ? { detail: r.details } : undefined,
  }));
  const sevCounts = summarize(structured);

  // Print results
  if (results.length === 0) {
    console.log(chalk.green.bold('  ✓ No vulnerabilities detected in basic scan!'));
    console.log(chalk.dim('  Note: This is a basic scan. Full pentest requires manual review.\n'));
    return {
      type: 'pentest',
      target: targetUrl,
      findings: [],
      severity_summary: sevCounts,
      summary: 'No vulnerabilities detected',
    };
  }

  const critical = results.filter(r => r.severity === 'critical');
  const high = results.filter(r => r.severity === 'high');
  const medium = results.filter(r => r.severity === 'medium');
  const low = results.filter(r => r.severity === 'low');

  console.log(chalk.white.bold('  Penetration Test Results'));
  console.log(chalk.gray('  ' + '─'.repeat(70)));
  console.log(
    `  ${chalk.red.bold(critical.length + ' critical')}  ` +
    `${chalk.red(high.length + ' high')}  ` +
    `${chalk.yellow(medium.length + ' medium')}  ` +
    `${chalk.gray(low.length + ' low')}`,
  );
  console.log(chalk.gray('  ' + '─'.repeat(70)));
  console.log();

  const sorted = [...critical, ...high, ...medium, ...low];
  for (const r of sorted) {
    const sev =
      r.severity === 'critical' ? chalk.bgRed.white.bold(` ${r.severity.toUpperCase()} `) :
      r.severity === 'high' ? chalk.red(`[${r.severity.toUpperCase()}]`) :
      r.severity === 'medium' ? chalk.yellow(`[${r.severity.toUpperCase()}]`) :
      chalk.gray(`[${r.severity.toUpperCase()}]`);

    console.log(`  ${sev} ${chalk.white.bold(r.type)}`);
    console.log(`    ${chalk.gray('URL:')}  ${chalk.cyan(r.url)}`);
    console.log(`    ${chalk.gray('Info:')} ${r.message}`);
    if (r.details) console.log(`    ${chalk.gray('Details:')} ${r.details}`);
    console.log();
  }

  console.log(chalk.gray('  ' + '─'.repeat(70)));
  console.log(`  ${chalk.white.bold(`${results.length} issue(s) found`)}`);
  console.log(chalk.dim('  Note: This is an automated basic scan. Manual review recommended.\n'));

  return {
    type: 'pentest',
    target: targetUrl,
    findings: structured,
    severity_summary: sevCounts,
    summary: `${results.length} issue(s): ${sevCounts.critical}C ${sevCounts.high}H ${sevCounts.medium}M ${sevCounts.low}L`,
  };
}
